import { useSearchParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import {
  FaCheckCircle,
  FaTimesCircle,
  FaShieldAlt,
  FaSpinner,
  FaFileSignature,
  FaIdCard,
  FaBuilding,
} from 'react-icons/fa';
import { certificatesValidationApi } from '../../api/certificatesValidation.api';
// Decodes a Base64url token back to { folio, tipo }
function decodeToken(token) {
  try {
    const b64 = token.replace(/-/g, '+').replace(/_/g, '/');
    const decoded = atob(b64);
    const [folio, tipo] = decoded.split('|');
    return folio && tipo ? { folio, tipo } : null;
  } catch {
    return null;
  }
}

const TIPO_LABEL = {
  ciudadano: 'EXPEDIENTE ÚNICO CIUDADANO',
  empresarial: 'CERTIFICADO EMPRESARIAL',
  patrimonial: 'REGISTRO PATRIMONIAL',
};

const TIPO_ICON = {
  ciudadano: <FaIdCard className="text-4xl opacity-80" />,
  empresarial: <FaBuilding className="text-4xl opacity-80" />,
  patrimonial: <FaFileSignature className="text-4xl opacity-80" />,
};

function Row({ label, value }) {
  return (
    <div className="flex flex-col py-3 border-b border-dashed border-gray-200 last:border-0">
      <span className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-1">
        {label}
      </span>
      <span className="text-gray-900 font-medium break-words">{value}</span>
    </div>
  );
}

function Validate() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('t');

  const [status, setStatus] = useState('loading');
  const [cert, setCert] = useState(null);
  const [tipo, setTipo] = useState(null);

  useEffect(() => {
    if (!token) {
      setStatus('invalid');
      return;
    }

    const parsed = decodeToken(token);
    if (!parsed) {
      setStatus('invalid');
      return;
    }

    const { folio, tipo: certTipo } = parsed;
    setTipo(certTipo);

    const fetchCertificado = async () => {
      try {
        const data = await certificatesValidationApi.getByFolio(folio);
        if (data) {
          setCert(data);
          setStatus('valid');
        } else {
          setStatus('invalid');
        }
      } catch {
        setStatus('error');
      }
    };

    fetchCertificado();
  }, [token]);

  const tipoKey = tipo?.toLowerCase() || '';
  const tipoLabel = TIPO_LABEL[tipoKey] || tipo?.toUpperCase() || 'CERTIFICADO DIGITAL';
  const Icon = TIPO_ICON[tipoKey] || <FaShieldAlt className="text-4xl opacity-80" />;

  const isVigente = cert?.estado?.toLowerCase() === 'vigente';
  const fechaFmt = cert?.createdAt
    ? new Date(cert.createdAt).toLocaleString('es-MX', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      })
    : '';

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4 font-sans text-gray-800">
      <div className="w-full max-w-4xl bg-white rounded-[24px] shadow-2xl overflow-hidden animate-[fade_0.4s_ease]">
        {/* ── Header ─────────────────────────────────── */}
        <div className="bg-gradient-to-br from-primary to-blue-800 text-white p-8 md:p-10 relative overflow-hidden">
          {/* Decorative Background Elements */}
          <div className="absolute top-0 right-0 -mr-10 -mt-10 opacity-10">
            <FaShieldAlt className="text-[200px]" />
          </div>

          <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="flex items-center gap-6">
              <div className="hidden md:flex h-16 w-16 bg-white/10 rounded-2xl items-center justify-center backdrop-blur-sm border border-white/20">
                {Icon}
              </div>
              <div>
                <div className="text-blue-200 text-sm font-bold tracking-widest mb-1 uppercase">
                  VERIFICACIÓN OFICIAL
                </div>
                <h1 className="text-3xl md:text-4xl font-extrabold m-0 leading-tight">
                  {tipoLabel}
                </h1>
                <p className="text-blue-100 mt-2 text-sm max-w-md">
                  Plataforma de Certificación Digital — Los datos, tus trámites. Un solo expediente.
                </p>
              </div>
            </div>
            {cert && (
              <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-xl p-4 text-right min-w-[200px]">
                <div className="text-blue-200 text-xs font-bold tracking-wider mb-1">
                  FOLIO DEL DOCUMENTO
                </div>
                <div className="text-2xl font-mono font-bold tracking-wider">{cert.folio}</div>
              </div>
            )}
          </div>
        </div>

        {/* ── Body ───────────────────────────────────── */}
        <div className="p-8 md:p-10 bg-white">
          {status === 'loading' && (
            <div className="flex flex-col items-center justify-center py-20 text-gray-400">
              <FaSpinner className="text-5xl animate-spin text-primary mb-6" />
              <h2 className="text-xl font-medium text-gray-600">Verificando certificado...</h2>
              <p className="text-sm mt-2">Conectando con el registro oficial</p>
            </div>
          )}

          {status === 'invalid' && (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <div className="h-24 w-24 bg-red-100 text-red-500 rounded-full flex items-center justify-center mb-6">
                <FaTimesCircle className="text-5xl" />
              </div>
              <h2 className="text-3xl font-bold text-gray-800 mb-2">Certificado No Encontrado</h2>
              <p className="text-gray-500 max-w-md">
                El código escaneado no corresponde a ningún documento emitido por esta plataforma
                oficial o ha sido alterado.
              </p>
            </div>
          )}

          {status === 'error' && (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <div className="h-24 w-24 bg-orange-100 text-orange-500 rounded-full flex items-center justify-center mb-6">
                <FaTimesCircle className="text-5xl" />
              </div>
              <h2 className="text-3xl font-bold text-gray-800 mb-2">Error de Conexión</h2>
              <p className="text-gray-500 max-w-md">
                No fue posible conectar con el servidor para verificar el certificado. Por favor,
                intenta de nuevo más tarde.
              </p>
            </div>
          )}

          {status === 'valid' && cert && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Columna Izquierda */}
              <div className="flex flex-col gap-8">
                {/* Datos del Titular */}
                <div>
                  <h3 className="text-lg font-bold text-primary border-b-2 border-gray-100 pb-2 mb-4 flex items-center gap-2">
                    DATOS DEL TITULAR
                  </h3>
                  <div className="bg-gray-50 rounded-2xl p-6 border border-gray-100">
                    <Row label="Nombre / Razón Social" value={cert.titular} />
                    <Row label="CURP" value={cert.curp || '—'} />
                    <Row label="RFC" value={cert.rfc || '—'} />
                    <div className="flex flex-col py-3 pt-4">
                      <span className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-2">
                        Estado del Documento
                      </span>
                      <div>
                        <span
                          className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-bold shadow-sm ${cert.esVigente ? 'bg-green-100 text-green-700 border border-green-200' : 'bg-red-100 text-red-700 border border-red-200'}`}
                        >
                          {cert.esValidoInteroperabilidad ? <FaCheckCircle /> : <FaTimesCircle />}
                          {cert.esVigente ? 'VIGENTE' : 'NO VIGENTE'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Columna Derecha */}
              <div className="flex flex-col gap-8">
                {/* Resultado de Validación (Destacado) */}

                <div className="bg-green-50 border-2 border-green-200 rounded-2xl p-6 text-center shadow-[0_0_20px_rgba(34,197,94,0.1)] relative overflow-hidden">
                  <div className="absolute top-0 right-0 -mr-4 -mt-4 opacity-10 text-green-500">
                    <FaShieldAlt className="text-8xl" />
                  </div>
                  <div className={`${cert.esValidoInteroperabilidad?'h-16 w-16 bg-green-500 text-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg shadow-green-500/30':'h-16 w-16 bg-red-500 text-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg shadow-red-500/30'}`}>
                    {cert.esValidoInteroperabilidad ? (
                      <FaCheckCircle className="text-3xl" />
                    ) : (
                      <FaTimesCircle className="text-3xl" />
                    )}
                  </div>
                  <h3 className="text-2xl font-black text-green-800 mb-1">
                    {cert.esValidoInteroperabilidad
                      ? ' Certificado Válido'
                      : 'Certificado no valido'}
                  </h3>
                  <p className="text-green-700 text-sm font-medium">
                    {cert.esValidoInteroperabilidad
                      ? 'Este documento ha sido verificado electrónicamente y su contenido es auténtico e inalterado.'
                      : 'Este documento ha sido verificado electrónicamente y su contenido no es auténtico.'}
                  </p>
                </div>

                {/* Seguridad */}
                <div>
                  <h3 className="text-lg font-bold text-primary border-b-2 border-gray-100 pb-2 mb-4 flex items-center gap-2">
                    INFORMACIÓN DE SEGURIDAD
                  </h3>
                  <div className="bg-gray-50 rounded-2xl p-6 border border-gray-100">
                    <Row
                      label="UUID de Certificación"
                      value={
                        <span className="font-mono text-sm break-all">{cert.securityUuid}</span>
                      }
                    />
                    <Row label="Fecha y Hora de Emisión" value={fechaFmt} />
                    <div className="flex flex-col py-3 border-b border-dashed border-gray-200 last:border-0">
                      <span className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-1">
                        Sello Digital
                      </span>
                      <span className="font-mono text-[10px] leading-tight text-gray-500 break-all bg-white p-3 rounded-lg border border-gray-200 shadow-inner max-h-32 overflow-y-auto">
                        {cert.digitalSeal}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ── Footer ─────────────────────────────────── */}
        <div className="bg-gray-50 border-t border-gray-200 p-6 md:px-10 text-center text-sm text-gray-500 flex flex-col items-center gap-2">
          <FaShieldAlt className="text-gray-300 text-2xl mb-1" />
          <p className="max-w-xl">
            {cert
              ? 'El presente documento ha sido firmado electrónicamente. Su validación garantiza la integridad y autoría del mismo, siendo válido para cualquier trámite oficial sin necesidad de firma autógrafa.'
              : 'Plataforma de Certificación Digital.'}
          </p>
          <div className="font-bold text-gray-400 mt-2 tracking-widest text-xs">
            GOBIERNO DEL ESTADO DE QUERÉTARO
          </div>
        </div>
      </div>
    </div>
  );
}

export default Validate;
